/**
 * Convert the percent-encoded UTF-8 produced by `encodeURIComponent` into a
 * binary string holding one character per byte (what `btoa` accepts).
 * Equivalent to the deprecated `unescape(encodeURIComponent(str))` idiom.
 */
const percentEncodedToBinary = (encoded: string): string =>
    encoded.replaceAll(/%([0-9A-F]{2})/g, (_match, hex: string) =>
        String.fromCodePoint(Number.parseInt(hex, 16)),
    )

/**
 * Percent-encode every byte of a binary string (one character per byte, as
 * returned by `atob`) so `decodeURIComponent` can decode it as UTF-8.
 * Equivalent to the deprecated `escape(binary)` for this purpose.
 */
const binaryToPercentEncoded = (binary: string): string =>
    Array.from(
        binary,
        (char) =>
            '%' + (char.codePointAt(0) ?? 0).toString(16).padStart(2, '0'),
    ).join('')

/**
 * Simple Base64 encoding for basic obfuscation
 * Note: This is NOT secure encryption - it's just encoding
 * For production, use proper encryption like AES or RSA
 * @param data The data to encode (string or object)
 * @returns Base64 encoded string
 */
export function encodeBase64(data: any): string {
    try {
        // Convert data to string if it's an object
        const dataStr = typeof data === 'string' ? data : JSON.stringify(data)
        // Encode to Base64
        return btoa(percentEncodedToBinary(encodeURIComponent(dataStr)))
    } catch (error) {
        throw new Error('Failed to encode data', { cause: error })
    }
}

/**
 * Decodes Base64 string back to original data
 * @param encodedStr Base64 encoded string
 * @returns Decoded string or object (if it was a JSON string)
 */
export function decodeBase64(encodedStr: string): any {
    try {
        // Decode from Base64
        const decodedStr = decodeURIComponent(
            binaryToPercentEncoded(atob(encodedStr)),
        )

        // Try to parse as JSON, return as is if not valid JSON
        try {
            return JSON.parse(decodedStr)
        } catch {
            return decodedStr
        }
    } catch (error) {
        throw new Error('Failed to decode data', { cause: error })
    }
}
